/**
 * Author: Fleming Shi
 * Project: DAL (Digital Abstraction Layer)
 * Date: 2025-11-17
 * Revision: 1.2
 * Description: Data Digest - Browse and digest files from OneDrive/SharePoint
 */

import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Folder,
  File,
  Check,
  X,
  RefreshCw,
  ChevronRight,
  ChevronLeft,
  ChevronsLeft,
  ChevronsRight,
  Loader,
  HardDrive,
  Users,
  Globe,
  Search,
  Home,
  ArrowLeft,
  Database,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  CheckCircle,
  AlertCircle,
  RotateCcw,
  Trash2,
  Clock,
  ListChecks,
  Ban,
} from 'lucide-react'
import { toast } from 'react-toastify'
import { api, type DigestJob } from '@services/api'
import { useAuth } from '@context/AuthContext'
import { useUserContext } from '@context/UserContext'

type SourceTab = 'onedrive' | 'shared' | 'sharepoint'

interface FileNode {
  id: string
  name: string
  type: 'file' | 'folder'
  path: string
  size?: number
  mimeType?: string
  lastModified?: string
  webUrl?: string
  driveId?: string
  siteId?: string
  source?: 'onedrive' | 'shared' | 'sharepoint'
  children?: FileNode[]
  expanded?: boolean
  loading?: boolean
}

interface SharePointSite {
  id: string
  name: string
  displayName: string
  webUrl: string
}

interface DriveInfo {
  id: string
  name: string
  driveType: string
  webUrl?: string
}

interface BreadcrumbItem {
  id: string
  name: string
  driveId?: string
  type: 'root' | 'site' | 'library' | 'folder'
}

interface Classification {
  label: string
  confidence: number
}

interface DigestedFile {
  path: string
  // 'skipped' = unsupported type, or over the file-size limit — not an error
  status: 'success' | 'failed' | 'skipped'
  content?: string
  error?: string
  timestamp: string
  fileType?: string
  summary?: string
  classifications?: Classification[]
}

interface StoredDocument {
  id: number
  file_name: string
  file_path: string
  content: string
  summary?: string
  classifications?: Classification[]
  source: string
  /** Who ingested it. Documents belong to the context, not to this person. */
  user_email?: string
  created_at: string
  updated_at: string
}

interface SelectedItem {
  id: string
  driveId?: string
  source: SourceTab
  name: string
  type: 'file' | 'folder'
}

type SortColumn = 'name' | 'lastModified' | 'size'
type SortDirection = 'asc' | 'desc'

const CLASSIFICATION_COLORS: Record<string, { bg: string; text: string }> = {
  // Sensitivity labels
  PCI: { bg: 'bg-red-100 dark:bg-red-900/40', text: 'text-red-800 dark:text-red-300' },
  PII: { bg: 'bg-red-100 dark:bg-red-900/40', text: 'text-red-700 dark:text-red-300' },
  PHI: { bg: 'bg-pink-100 dark:bg-pink-900/40', text: 'text-pink-700 dark:text-pink-300' },
  CPNI: { bg: 'bg-orange-100 dark:bg-orange-900/40', text: 'text-orange-700 dark:text-orange-300' },
  CONFIDENTIAL: { bg: 'bg-yellow-100 dark:bg-yellow-900/40', text: 'text-yellow-800 dark:text-yellow-300' },
  INTERNAL: { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-700 dark:text-gray-300' },
  PUBLIC: { bg: 'bg-green-100 dark:bg-green-900/40', text: 'text-green-700 dark:text-green-300' },
  // Business category labels
  Finance: { bg: 'bg-emerald-100 dark:bg-emerald-900/40', text: 'text-emerald-700 dark:text-emerald-300' },
  IT: { bg: 'bg-indigo-100 dark:bg-indigo-900/40', text: 'text-indigo-700 dark:text-indigo-300' },
  'Service Assurance': { bg: 'bg-cyan-100 dark:bg-cyan-900/40', text: 'text-cyan-700 dark:text-cyan-300' },
  'Service Delivery': { bg: 'bg-violet-100 dark:bg-violet-900/40', text: 'text-violet-700 dark:text-violet-300' },
  SLA: { bg: 'bg-blue-100 dark:bg-blue-900/40', text: 'text-blue-700 dark:text-blue-300' },
}

const CATEGORY_LABELS = new Set(['Finance', 'IT', 'Service Assurance', 'Service Delivery', 'SLA'])

const renderClassificationBadges = (classifications?: Classification[]) => {
  if (!classifications || classifications.length === 0) {
    return <span className="text-xs text-gray-400 italic dark:text-gray-500">Unclassified</span>
  }
  const categories = classifications.filter((c) => CATEGORY_LABELS.has(c.label))
  const sensitivity = classifications.filter((c) => !CATEGORY_LABELS.has(c.label))
  return (
    <div className="flex flex-wrap gap-1">
      {categories.map((c) => {
        const colors = CLASSIFICATION_COLORS[c.label] || {
          bg: 'bg-gray-100 dark:bg-gray-700',
          text: 'text-gray-700 dark:text-gray-300',
        }
        return (
          <span
            key={c.label}
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${colors.bg} ${colors.text} ring-1 ring-current/20 ring-inset`}
            title={`Category: ${c.label} (${Math.round(c.confidence * 100)}%)`}
          >
            {c.label}
          </span>
        )
      })}
      {sensitivity.map((c) => {
        const colors = CLASSIFICATION_COLORS[c.label] || {
          bg: 'bg-gray-100 dark:bg-gray-700',
          text: 'text-gray-700 dark:text-gray-300',
        }
        return (
          <span
            key={c.label}
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${colors.bg} ${colors.text}`}
            title={`Sensitivity: ${c.label} (${Math.round(c.confidence * 100)}%)`}
          >
            {c.label}
          </span>
        )
      })}
    </div>
  )
}

const ContextDigester: React.FC = () => {
  // Auth state
  const { user } = useAuth()
  const { activeContext, availableContexts } = useUserContext()
  // Superadmins (members of the 'superadmin' context) can wipe a whole domain.
  const isSuperadmin = availableContexts.some((c) => c.name === 'superadmin')

  // Source tab state - load from localStorage
  const [activeTab, setActiveTab] = useState<SourceTab>(() => {
    const saved = localStorage.getItem('activeSourceTab')
    return (saved as SourceTab) || 'onedrive'
  })

  // Save active tab to localStorage
  useEffect(() => {
    localStorage.setItem('activeSourceTab', activeTab)
  }, [activeTab])

  // File browser state
  const [files, setFiles] = useState<FileNode[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedItems, setSelectedItems] = useState<Map<string, SelectedItem>>(new Map())

  // SharePoint specific state
  const [sites, setSites] = useState<SharePointSite[]>([])
  const [selectedSite, setSelectedSite] = useState<SharePointSite | null>(null)
  const [libraries, setLibraries] = useState<DriveInfo[]>([])
  const [selectedLibrary, setSelectedLibrary] = useState<DriveInfo | null>(null)
  const [siteSearchQuery, setSiteSearchQuery] = useState('')
  const [siteUrlQuery, setSiteUrlQuery] = useState('')

  // Navigation state - persist per tab
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([])
  const [currentDriveId, setCurrentDriveId] = useState<string | undefined>()

  // Navigation context storage per tab - load from localStorage
  const [navigationContext, setNavigationContext] = useState<{
    [key in SourceTab]?: {
      breadcrumbs: BreadcrumbItem[]
      currentDriveId?: string
      files: FileNode[]
      selectedSite?: SharePointSite | null
      selectedLibrary?: DriveInfo | null
      libraries?: DriveInfo[]
    }
  }>(() => {
    const saved = localStorage.getItem('navigationContext')
    return saved ? JSON.parse(saved) : {}
  })

  // Save navigation context to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('navigationContext', JSON.stringify(navigationContext))
  }, [navigationContext])

  // Save current navigation state to context whenever breadcrumbs or files change
  useEffect(() => {
    if (breadcrumbs.length > 0) {
      setNavigationContext((prev) => ({
        ...prev,
        [activeTab]: {
          breadcrumbs,
          currentDriveId,
          files,
          selectedSite,
          selectedLibrary,
          libraries,
        },
      }))
    }
  }, [breadcrumbs, files, currentDriveId, selectedSite, selectedLibrary, libraries, activeTab])

  // Digest state — submitting only covers the (fast) queueing call; the actual
  // work is tracked in the jobs panel below, so the browser is never blocked.
  const [submitting, setSubmitting] = useState(false)
  const [jobs, setJobs] = useState<DigestJob[]>([])
  const [jobsLoading, setJobsLoading] = useState(false)
  // True when the last refresh failed — the panel keeps showing the previous
  // state, but says so rather than looking authoritative.
  const [jobsStale, setJobsStale] = useState(false)
  const [purgingDomain, setPurgingDomain] = useState(false)
  // Recent activity is per-context: a file digested into Sales has no business
  // showing up in the CPQ view. Stored under a context-keyed localStorage entry
  // rather than one global list.
  const digestedFilesKey = `digestedFiles:${activeContext?.id ?? 'none'}`
  const [digestedFiles, setDigestedFiles] = useState<DigestedFile[]>(() => {
    const saved = localStorage.getItem(`digestedFiles:${activeContext?.id ?? 'none'}`)
    return saved ? JSON.parse(saved) : []
  })

  // Sort state
  const [sortColumn, setSortColumn] = useState<SortColumn>('name')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')

  // Stored documents state (from database)
  const [storedDocuments, setStoredDocuments] = useState<StoredDocument[]>([])
  const [storedDocsMap, setStoredDocsMap] = useState<Map<string, StoredDocument>>(new Map())

  // Digested files table pagination and selection
  const [digestedPage, setDigestedPage] = useState(1)
  const [digestedPageSize] = useState(10)
  const [selectedDigestedFiles, setSelectedDigestedFiles] = useState<Set<number>>(new Set())
  const [resyncInProgress, setResyncInProgress] = useState(false)

  // Digested files filter + search
  const [digestedSearch, setDigestedSearch] = useState('')
  const [digestedClassFilter, setDigestedClassFilter] = useState('all')

  // Reset to first page whenever the search or filter changes
  useEffect(() => {
    setDigestedPage(1)
  }, [digestedSearch, digestedClassFilter])

  // Save digested files to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(digestedFilesKey, JSON.stringify(digestedFiles))
  }, [digestedFiles, digestedFilesKey])

  // Load stored documents on mount and create lookup map
  const loadStoredDocuments = useCallback(async () => {
    try {
      const response = await api.getStoredDocuments()
      setStoredDocuments(response.documents)

      // Create a map for quick lookup by file_name
      const docsMap = new Map<string, StoredDocument>()
      response.documents.forEach((doc) => {
        docsMap.set(doc.file_name, doc)
      })
      setStoredDocsMap(docsMap)
    } catch (error) {
      console.error('Error loading stored documents:', error)
    }
  }, [])

  // Load stored documents on mount
  useEffect(() => {
    loadStoredDocuments()
  }, [loadStoredDocuments])

  // ── Digest jobs panel ──────────────────────────────────────────────────────
  // Jobs run server-side on a queue, so the panel is just a poll of /jobs. It
  // polls fast while anything is live and stops once everything has settled, so
  // an idle page costs nothing.
  const loadJobs = useCallback(async (showSpinner = false) => {
    if (showSpinner) setJobsLoading(true)
    try {
      const response = await api.getDigestJobs()
      setJobs(response.jobs)
      setJobsStale(false)
      return response.jobs
    } catch (error) {
      // Deliberately does NOT clear `jobs` — a failed refresh should leave the
      // last known state on screen, flagged as stale, not blank the panel.
      console.error('Error loading digest jobs:', error)
      setJobsStale(true)
      return null
    } finally {
      if (showSpinner) setJobsLoading(false)
    }
  }, [])

  // Refresh on mount, and again whenever the page is revealed — coming back to
  // the tab or the route should show current state immediately rather than
  // waiting out a poll interval.
  useEffect(() => {
    loadJobs()

    const refreshIfVisible = () => {
      if (document.visibilityState === 'visible') loadJobs()
    }
    document.addEventListener('visibilitychange', refreshIfVisible)
    window.addEventListener('focus', refreshIfVisible)
    return () => {
      document.removeEventListener('visibilitychange', refreshIfVisible)
      window.removeEventListener('focus', refreshIfVisible)
    }
  }, [loadJobs])

  // Poll on a plain interval keyed only on *whether* anything is live.
  //
  // The obvious implementation — schedule the next poll from an effect that
  // depends on `jobs` — is subtly broken: a failed request never calls setJobs,
  // so the array reference doesn't change, the effect doesn't re-run, and no
  // next poll is ever scheduled. One transient blip would freeze the panel for
  // the rest of a multi-hour run, showing a job as "Running" long after it
  // finished. An interval keeps ticking regardless and self-heals.
  const hasLiveJobs = jobs.some((j) => j.status === 'pending' || j.status === 'processing')

  useEffect(() => {
    if (!hasLiveJobs) return
    const id = setInterval(() => loadJobs(), 2500)
    return () => clearInterval(id)
  }, [hasLiveJobs, loadJobs])

  // Remember which jobs were live last tick so the documents table can refresh
  // exactly once, when a job actually finishes.
  const liveJobIdsRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    const live = jobs.filter((j) => j.status === 'pending' || j.status === 'processing')
    const liveIds = new Set(live.map((j) => j.jobId))
    const justFinished = [...liveJobIdsRef.current].filter((id) => !liveIds.has(id))
    liveJobIdsRef.current = liveIds

    if (justFinished.length > 0) {
      loadStoredDocuments()
      for (const id of justFinished) {
        const job = jobs.find((j) => j.jobId === id)
        if (!job) continue
        if (job.status === 'completed') {
          // Pull the per-file results so they show up in the recent-activity list,
          // and break the outcome down — on a big run "500/500 files" hides the
          // fact that half were skipped as oversized or unsupported.
          api
            .getDigestJobStatus(job.jobId)
            .then((detail) => {
              const results = detail.results ?? []
              if (results.length) setDigestedFiles((prev) => [...results, ...prev])
              const ok = results.filter((r: any) => r.status === 'success').length
              const skipped = results.filter((r: any) => r.status === 'skipped').length
              const failed = results.filter((r: any) => r.status === 'failed').length
              const parts = [`${ok} digested`]
              if (skipped) parts.push(`${skipped} skipped`)
              if (failed) parts.push(`${failed} failed`)
              toast.success(`Digest finished: ${job.label || job.jobId} — ${parts.join(', ')}`)
            })
            .catch(() => {
              toast.success(`Digest finished: ${job.label || job.jobId} (${job.completed}/${job.total} files)`)
            })
        } else if (job.status === 'failed') {
          toast.error(`Digest failed: ${job.label || job.jobId} — ${job.error || 'unknown error'}`)
        }
      }
    }
  }, [jobs, loadStoredDocuments])

  // ── Context switching ──────────────────────────────────────────────────────
  // Everything on this page is scoped to the active context: the documents, the
  // jobs, the recent-activity list, and where a new digest will land. Switching
  // context previously left all of it on screen, so you could be looking at
  // Sales' documents while queueing into CPQ.
  const previousContextId = useRef<number | undefined>(activeContext?.id)

  useEffect(() => {
    const contextId = activeContext?.id
    if (contextId === undefined || previousContextId.current === contextId) return

    const from = previousContextId.current
    previousContextId.current = contextId

    // Selections refer to the old view — carrying them across would queue work
    // the user no longer means to queue.
    setSelectedItems(new Map())
    setSelectedDigestedFiles(new Set())
    setDigestedPage(1)
    setDigestedSearch('')
    setDigestedClassFilter('all')

    // Swap in this context's recent-activity list.
    const saved = localStorage.getItem(`digestedFiles:${contextId}`)
    setDigestedFiles(saved ? JSON.parse(saved) : [])

    // Blank the tables while the new context loads, so stale rows are never
    // shown as though they belonged to the context you just switched to.
    setStoredDocuments([])
    setStoredDocsMap(new Map())
    setJobs([])

    void loadStoredDocuments()
    void loadJobs(true)

    if (from !== undefined) {
      toast.info(`Switched to ${activeContext?.display_name ?? 'context'} — showing its documents and jobs`)
    }
  }, [activeContext?.id, activeContext?.display_name, loadStoredDocuments, loadJobs])

  const handleCancelJob = async (job: DigestJob) => {
    try {
      const { action } = await api.cancelDigestJob(job.jobId)
      toast.info(action === 'removed' ? 'Job removed from the list' : 'Job cancelled')
      await loadJobs()
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to cancel job')
    }
  }

  const handleClearFinishedJobs = async () => {
    try {
      const { deletedCount } = await api.clearFinishedDigestJobs()
      toast.info(`Cleared ${deletedCount} finished job${deletedCount === 1 ? '' : 's'}`)
      await loadJobs()
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to clear jobs')
    }
  }

  // Save current navigation context before switching tabs
  const saveNavigationContext = useCallback(
    (tab: SourceTab) => {
      setNavigationContext((prev) => ({
        ...prev,
        [tab]: {
          breadcrumbs,
          currentDriveId,
          files,
          selectedSite,
          selectedLibrary,
          libraries,
        },
      }))
    },
    [breadcrumbs, currentDriveId, files, selectedSite, selectedLibrary, libraries],
  )

  // Restore navigation context for a tab
  const restoreNavigationContext = useCallback(
    (tab: SourceTab): boolean => {
      const context = navigationContext[tab]
      if (context && context.breadcrumbs.length > 0) {
        setBreadcrumbs(context.breadcrumbs)
        setCurrentDriveId(context.currentDriveId)
        setFiles(context.files)
        if (tab === 'sharepoint') {
          setSelectedSite(context.selectedSite || null)
          setSelectedLibrary(context.selectedLibrary || null)
          setLibraries(context.libraries || [])
        }
        return true
      }
      return false
    },
    [navigationContext],
  )

  // Handle tab change with context preservation
  const handleTabChange = useCallback(
    (newTab: SourceTab) => {
      if (newTab === activeTab) {
        return
      }

      // Save current tab's context
      saveNavigationContext(activeTab)

      // Switch to new tab
      setActiveTab(newTab)
    },
    [activeTab, saveNavigationContext],
  )

  // Load files when tab changes
  useEffect(() => {
    // Try to restore context first
    const restored = restoreNavigationContext(activeTab)
    if (!restored) {
      // No saved context, load fresh
      loadSourceContent()
    }
  }, [activeTab])

  const loadSourceContent = useCallback(async () => {
    setLoading(true)
    setFiles([])
    setBreadcrumbs([])
    setCurrentDriveId(undefined)

    try {
      switch (activeTab) {
        case 'onedrive':
          await loadOneDriveFiles()
          break
        case 'shared':
          await loadSharedFiles()
          break
        case 'sharepoint':
          await loadSharePointSites()
          break
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || `Failed to load ${activeTab} content`)
      console.error('Error loading content:', error)
    } finally {
      setLoading(false)
    }
  }, [activeTab])

  // Refresh current folder without losing navigation context
  const refreshCurrentFolder = useCallback(async () => {
    setLoading(true)
    try {
      // Get the current folder from breadcrumbs
      const currentBreadcrumb = breadcrumbs[breadcrumbs.length - 1]

      if (!currentBreadcrumb || currentBreadcrumb.type === 'root') {
        // At root level, reload the source content
        if (activeTab === 'onedrive') {
          const response = await api.browseOneDriveFiles()
          setFiles(response.files)
        } else if (activeTab === 'shared') {
          const response = await api.browseSharedFiles()
          setFiles(response.files)
        } else if (activeTab === 'sharepoint') {
          if (selectedLibrary && currentDriveId) {
            // In a library
            const lastFolder = breadcrumbs.filter((b) => b.type === 'folder').pop()
            const response = await api.browseSharePointLibrary(currentDriveId, lastFolder?.id)
            setFiles(response.files)
          } else if (selectedSite) {
            // At library selection level
            const response = await api.getSiteDocumentLibraries(selectedSite.id)
            setLibraries(response.libraries)
          } else {
            // At site selection level
            const response = await api.getSharePointSites()
            setSites(response.sites)
          }
        }
      } else if (currentBreadcrumb.type === 'folder') {
        // In a folder, reload that folder
        if (activeTab === 'onedrive') {
          const response = await api.browseOneDriveFiles(currentBreadcrumb.id)
          setFiles(response.files)
        } else if (activeTab === 'shared' && currentBreadcrumb.driveId) {
          const response = await api.browseSharedFolder(currentBreadcrumb.driveId, currentBreadcrumb.id)
          setFiles(response.files)
        } else if (activeTab === 'sharepoint' && currentDriveId) {
          const response = await api.browseSharePointLibrary(currentDriveId, currentBreadcrumb.id)
          setFiles(response.files)
        }
      } else if (currentBreadcrumb.type === 'library' && currentDriveId) {
        // At library root
        const response = await api.browseSharePointLibrary(currentDriveId)
        setFiles(response.files)
      } else if (currentBreadcrumb.type === 'site' && selectedSite) {
        // At site level (showing libraries)
        const response = await api.getSiteDocumentLibraries(selectedSite.id)
        setLibraries(response.libraries)
      }

      toast.success('Refreshed')
    } catch (error: any) {
      toast.error('Failed to refresh')
      console.error('Error refreshing:', error)
    } finally {
      setLoading(false)
    }
  }, [activeTab, breadcrumbs, currentDriveId, selectedSite, selectedLibrary])

  const loadOneDriveFiles = async (folderId?: string) => {
    const response = await api.browseOneDriveFiles(folderId)
    setFiles(response.files)
    if (!folderId) {
      setBreadcrumbs([{ id: 'root', name: 'My OneDrive', type: 'root' }])
    }
  }

  const loadSharedFiles = async () => {
    const response = await api.browseSharedFiles()
    setFiles(response.files)
    setBreadcrumbs([{ id: 'root', name: 'Shared with Me', type: 'root' }])
  }

  const loadSharePointSites = async (searchQuery?: string, siteUrl?: string) => {
    const response = await api.getSharePointSites(searchQuery, siteUrl)
    setSites(response.sites)
    setSelectedSite(null)
    setSelectedLibrary(null)
    setLibraries([])
    setFiles([])
    setBreadcrumbs([{ id: 'root', name: 'SharePoint Sites', type: 'root' }])
  }

  const loadSiteLibraries = async (site: SharePointSite) => {
    setLoading(true)
    try {
      const response = await api.getSiteDocumentLibraries(site.id)
      setLibraries(response.libraries)
      setSelectedSite(site)
      setSelectedLibrary(null)
      setFiles([])
      setBreadcrumbs([
        { id: 'root', name: 'SharePoint Sites', type: 'root' },
        { id: site.id, name: site.displayName, type: 'site' },
      ])
    } catch (error: any) {
      toast.error('Failed to load document libraries')
      console.error('Error loading libraries:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadLibraryFiles = async (library: DriveInfo, folderId?: string) => {
    setLoading(true)
    try {
      const response = await api.browseSharePointLibrary(library.id, folderId)
      setFiles(response.files)
      setSelectedLibrary(library)
      setCurrentDriveId(library.id)

      if (!folderId) {
        setBreadcrumbs((prev) => {
          const siteIndex = prev.findIndex((b) => b.type === 'site')
          return [
            ...prev.slice(0, siteIndex + 1),
            { id: library.id, name: library.name, driveId: library.id, type: 'library' },
          ]
        })
      }
    } catch (error: any) {
      toast.error('Failed to load library files')
      console.error('Error loading library files:', error)
    } finally {
      setLoading(false)
    }
  }

  const navigateToFolder = async (node: FileNode) => {
    if (node.type !== 'folder') {
      return
    }

    setLoading(true)
    try {
      let response

      if (activeTab === 'onedrive') {
        response = await api.browseOneDriveFiles(node.id)
      } else if (activeTab === 'shared') {
        if (node.driveId) {
          response = await api.browseSharedFolder(node.driveId, node.id)
          setCurrentDriveId(node.driveId)
        } else {
          toast.error('Cannot navigate into this folder')
          return
        }
      } else if (activeTab === 'sharepoint') {
        if (currentDriveId) {
          response = await api.browseSharePointLibrary(currentDriveId, node.id)
        } else {
          toast.error('No library selected')
          return
        }
      }

      if (response) {
        setFiles(response.files)
        setBreadcrumbs((prev) => [
          ...prev,
          { id: node.id, name: node.name, driveId: node.driveId || currentDriveId, type: 'folder' },
        ])
      }
    } catch (error: any) {
      toast.error('Failed to load folder contents')
      console.error('Error loading folder:', error)
    } finally {
      setLoading(false)
    }
  }

  const navigateToBreadcrumb = async (index: number) => {
    const breadcrumb = breadcrumbs[index]
    setLoading(true)

    try {
      if (breadcrumb.type === 'root') {
        await loadSourceContent()
        return
      }

      if (activeTab === 'sharepoint') {
        if (breadcrumb.type === 'site') {
          const site = sites.find((s) => s.id === breadcrumb.id)
          if (site) {
            await loadSiteLibraries(site)
          }
          return
        }

        if (breadcrumb.type === 'library') {
          const library = libraries.find((l) => l.id === breadcrumb.id)
          if (library) {
            await loadLibraryFiles(library)
          }
          return
        }
      }

      // Navigate to folder
      let response
      if (activeTab === 'onedrive') {
        response = await api.browseOneDriveFiles(breadcrumb.id === 'root' ? undefined : breadcrumb.id)
      } else if (activeTab === 'shared') {
        if (breadcrumb.driveId) {
          response = await api.browseSharedFolder(breadcrumb.driveId, breadcrumb.id)
        }
      } else if (activeTab === 'sharepoint' && breadcrumb.driveId) {
        response = await api.browseSharePointLibrary(breadcrumb.driveId, breadcrumb.id)
      }

      if (response) {
        setFiles(response.files)
        setBreadcrumbs(breadcrumbs.slice(0, index + 1))
      }
    } catch (error: any) {
      toast.error('Failed to navigate')
      console.error('Error navigating:', error)
    } finally {
      setLoading(false)
    }
  }

  const toggleSelection = (node: FileNode) => {
    const newSelected = new Map(selectedItems)
    const key = `${activeTab}-${node.id}`

    if (newSelected.has(key)) {
      newSelected.delete(key)
    } else {
      newSelected.set(key, {
        id: node.id,
        driveId: node.driveId || currentDriveId,
        source: activeTab,
        name: node.name,
        type: node.type,
      })
    }
    setSelectedItems(newSelected)
  }

  // Build current folder path from breadcrumbs
  const getCurrentFolderPath = () => {
    // Skip root breadcrumb and build path from folder breadcrumbs
    const folderBreadcrumbs = breadcrumbs.filter((b) => b.type === 'folder')
    if (folderBreadcrumbs.length === 0) {
      return ''
    }
    return folderBreadcrumbs.map((b) => b.name).join('/')
  }

  const handleDigest = async () => {
    if (selectedItems.size === 0) {
      toast.warning('Please select at least one file or folder')
      return
    }

    setSubmitting(true)
    try {
      const currentPath = getCurrentFolderPath()
      const selected = Array.from(selectedItems.values())
      const items = selected.map((item) => ({
        id: item.id,
        driveId: item.driveId,
        source: item.source,
        folderPath: currentPath, // Include current folder path
      }))

      // Name the job after where the files came from so a queue of several jobs
      // is readable at a glance.
      const where = currentPath.split('/').filter(Boolean).pop() || selectedLibrary?.name || selectedSite?.name
      const label = `${where || activeTab} — ${selected.length} item${selected.length === 1 ? '' : 's'}`

      const job = await api.digestFiles(items, label)
      toast.success(
        job.queuePosition > 0
          ? `Digest queued (#${job.queuePosition} in line) — track it in Digest Jobs`
          : 'Digest started — track it in Digest Jobs',
      )
      // Selection is consumed by the job; free it up so more can be queued.
      setSelectedItems(new Map())
      await loadJobs()
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to queue digest')
      console.error('Error queueing digest:', error)
    } finally {
      setSubmitting(false)
    }
  }

  const clearSelection = () => {
    setSelectedItems(new Map())
  }

  // Superadmin: wipe the entire active domain (all users' docs + vectors + jobs).
  const handlePurgeDomain = async () => {
    const domain = activeContext?.display_name || activeContext?.name || 'this domain'
    if (
      !window.confirm(
        `Permanently delete ALL digested documents, embeddings, and job state for the "${domain}" domain — across every user? This cannot be undone.`,
      )
    ) {
      return
    }
    setPurgingDomain(true)
    try {
      const res = await api.purgeContextDomain()
      setDigestedFiles([])
      localStorage.removeItem('digestedFiles')
      await loadStoredDocuments()
      toast.success(`Purged ${domain}: ${res.documents} document(s), ${res.vectorChunks} vector chunk(s)`)
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to purge domain')
    } finally {
      setPurgingDomain(false)
    }
  }

  const formatFileSize = (bytes?: number): string => {
    if (!bytes) {
      return ''
    }
    if (bytes < 1024) {
      return `${bytes} B`
    }
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const formatDate = (dateString?: string): string => {
    if (!dateString) {
      return ''
    }
    return new Date(dateString).toLocaleDateString()
  }

  // Get digest status for a file
  const getDigestStatus = (node: FileNode): 'digested' | 'sync-required' | null => {
    if (node.type === 'folder') {
      return null
    }

    const storedDoc = storedDocsMap.get(node.name)
    if (!storedDoc) {
      return null
    }

    // Compare file's lastModified with when it was digested
    if (node.lastModified && storedDoc.updated_at) {
      const fileModified = new Date(node.lastModified).getTime()
      const digestedAt = new Date(storedDoc.updated_at).getTime()

      // If file was modified after it was digested, sync is required
      if (fileModified > digestedAt) {
        return 'sync-required'
      }
    }

    return 'digested'
  }

  // Build a map of folders that have digested content
  const folderDigestMap = React.useMemo(() => {
    const map = new Map<string, { hasDigested: boolean; digestedAt: Date | null }>()

    storedDocuments.forEach((doc) => {
      // Extract folder names from the file path
      // file_name could be "FolderName/SubFolder/file.docx" or just "file.docx"
      const pathParts = doc.file_name.split('/')

      if (pathParts.length > 1) {
        // File is inside a folder - mark all parent folders as having digested content
        let currentPath = ''
        for (let i = 0; i < pathParts.length - 1; i++) {
          const folderName = pathParts[i]
          currentPath = currentPath ? `${currentPath}/${folderName}` : folderName

          const existing = map.get(folderName)
          const docDate = new Date(doc.updated_at)

          if (!existing) {
            map.set(folderName, { hasDigested: true, digestedAt: docDate })
          } else if (existing.digestedAt && docDate > existing.digestedAt) {
            map.set(folderName, { hasDigested: true, digestedAt: docDate })
          }
        }
      }
    })

    return map
  }, [storedDocuments])

  // Get digest status for a folder based on its contents
  const getFolderDigestStatus = (folderNode: FileNode): 'digested' | 'sync-required' | null => {
    if (folderNode.type !== 'folder') {
      return null
    }

    const folderName = folderNode.name
    const folderInfo = folderDigestMap.get(folderName)

    // Check if this folder has any digested content
    if (!folderInfo || !folderInfo.hasDigested) {
      return null
    }

    // Check if the folder was modified after the last digest
    if (folderNode.lastModified && folderInfo.digestedAt) {
      const folderModified = new Date(folderNode.lastModified).getTime()
      const digestedAt = folderInfo.digestedAt.getTime()

      // If folder was modified after last digest, sync may be required
      if (folderModified > digestedAt) {
        return 'sync-required'
      }
    }

    return 'digested'
  }

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortDirection('asc')
    }
  }

  const getSortedFiles = useCallback(() => {
    const sorted = [...files].sort((a, b) => {
      // Folders always come first
      if (a.type !== b.type) {
        return a.type === 'folder' ? -1 : 1
      }

      let comparison = 0

      switch (sortColumn) {
        case 'name':
          comparison = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
          break
        case 'lastModified': {
          const dateA = a.lastModified ? new Date(a.lastModified).getTime() : 0
          const dateB = b.lastModified ? new Date(b.lastModified).getTime() : 0
          comparison = dateA - dateB
          break
        }
        case 'size':
          comparison = (a.size || 0) - (b.size || 0)
          break
      }

      return sortDirection === 'asc' ? comparison : -comparison
    })

    return sorted
  }, [files, sortColumn, sortDirection])

  const renderSortIcon = (column: SortColumn) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400 dark:text-gray-500" />
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className="h-4 w-4 text-blue-600" />
    ) : (
      <ArrowDown className="h-4 w-4 text-blue-600" />
    )
  }

  const renderTabs = () => (
    <div className="mb-4 flex border-b border-gray-200 dark:border-gray-700">
      <button
        onClick={() => handleTabChange('onedrive')}
        className={`flex items-center gap-2 border-b-2 px-4 py-3 font-medium transition-colors ${
          activeTab === 'onedrive'
            ? 'border-blue-500 text-blue-600'
            : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
        }`}
      >
        <HardDrive className="h-4 w-4" />
        My OneDrive
      </button>
      <button
        onClick={() => handleTabChange('shared')}
        className={`flex items-center gap-2 border-b-2 px-4 py-3 font-medium transition-colors ${
          activeTab === 'shared'
            ? 'border-blue-500 text-blue-600'
            : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
        }`}
      >
        <Users className="h-4 w-4" />
        Shared with Me
      </button>
      <button
        onClick={() => handleTabChange('sharepoint')}
        className={`flex items-center gap-2 border-b-2 px-4 py-3 font-medium transition-colors ${
          activeTab === 'sharepoint'
            ? 'border-blue-500 text-blue-600'
            : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
        }`}
      >
        <Globe className="h-4 w-4" />
        SharePoint
      </button>
    </div>
  )

  const renderBreadcrumbs = () => (
    <div className="mb-4 flex items-center gap-1 overflow-x-auto text-sm">
      {breadcrumbs.map((breadcrumb, index) => (
        <React.Fragment key={breadcrumb.id}>
          {index > 0 && <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />}
          <button
            onClick={() => navigateToBreadcrumb(index)}
            className={`rounded px-2 py-1 whitespace-nowrap hover:bg-gray-100 dark:hover:bg-gray-700 ${
              index === breadcrumbs.length - 1 ? 'font-medium text-gray-900 dark:text-gray-100' : 'text-blue-600'
            }`}
          >
            {index === 0 && <Home className="mr-1 inline h-4 w-4" />}
            {breadcrumb.name}
          </button>
        </React.Fragment>
      ))}
    </div>
  )

  const renderSharePointSiteSelector = () => (
    <div className="space-y-4">
      {/* Add a specific site by its URL. Works even where tenant-wide site search
          is blocked by SharePoint governance (search then returns no results). */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Globe className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 transform text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            placeholder="Add a site by URL, e.g. https://yourorg.sharepoint.com/sites/Benefits"
            value={siteUrlQuery}
            onChange={(e) => setSiteUrlQuery(e.target.value)}
            onKeyDown={(e) =>
              e.key === 'Enter' && siteUrlQuery.trim() && loadSharePointSites(undefined, siteUrlQuery.trim())
            }
            className="w-full rounded-lg border border-gray-300 py-2 pr-4 pl-10 focus:border-transparent focus:ring-2 focus:ring-blue-500 dark:border-gray-600"
          />
        </div>
        <button
          onClick={() => siteUrlQuery.trim() && loadSharePointSites(undefined, siteUrlQuery.trim())}
          className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
        >
          Add site
        </button>
      </div>

      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 transform text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            placeholder="Search SharePoint sites (may be unavailable on some tenants)..."
            value={siteSearchQuery}
            onChange={(e) => setSiteSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && loadSharePointSites(siteSearchQuery)}
            className="w-full rounded-lg border border-gray-300 py-2 pr-4 pl-10 focus:border-transparent focus:ring-2 focus:ring-blue-500 dark:border-gray-600"
          />
        </div>
        <button
          onClick={() => loadSharePointSites(siteSearchQuery)}
          className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
        >
          Search
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {sites.map((site) => (
          <div
            key={site.id}
            onClick={() => loadSiteLibraries(site)}
            className="cursor-pointer rounded-lg border border-gray-200 p-4 transition-colors hover:border-blue-300 hover:bg-blue-50 dark:border-gray-700 dark:hover:bg-blue-900/30"
          >
            <div className="flex items-center gap-3">
              <Globe className="h-8 w-8 text-blue-500" />
              <div className="min-w-0 flex-1">
                <h3 className="truncate font-medium text-gray-900 dark:text-gray-100">{site.displayName}</h3>
                <p className="truncate text-sm text-gray-500 dark:text-gray-400">{site.webUrl}</p>
              </div>
            </div>
          </div>
        ))}
        {sites.length === 0 && !loading && (
          <div className="col-span-full py-8 text-center text-gray-500 dark:text-gray-400">
            No sites shown. This list shows sites you <strong>follow</strong> in SharePoint. To add another site, paste
            its URL above (search may be blocked by your tenant).
          </div>
        )}
      </div>
    </div>
  )

  const renderLibrarySelector = () => (
    <div className="space-y-4">
      <div className="mb-4 flex items-center gap-2">
        <button
          onClick={() => {
            setSelectedSite(null)
            setLibraries([])
            setBreadcrumbs([{ id: 'root', name: 'SharePoint Sites', type: 'root' }])
          }}
          className="flex items-center gap-1 text-blue-600 hover:text-blue-800 dark:hover:text-blue-300"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Sites
        </button>
      </div>

      <h3 className="text-lg font-medium">Document Libraries in {selectedSite?.displayName}</h3>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {libraries.map((library) => (
          <div
            key={library.id}
            onClick={() => loadLibraryFiles(library)}
            className="cursor-pointer rounded-lg border border-gray-200 p-4 transition-colors hover:border-blue-300 hover:bg-blue-50 dark:border-gray-700 dark:hover:bg-blue-900/30"
          >
            <div className="flex items-center gap-3">
              <Database className="h-8 w-8 text-orange-500" />
              <div className="min-w-0 flex-1">
                <h3 className="truncate font-medium text-gray-900 dark:text-gray-100">{library.name}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{library.driveType}</p>
              </div>
            </div>
          </div>
        ))}
        {libraries.length === 0 && !loading && (
          <div className="col-span-full py-8 text-center text-gray-500 dark:text-gray-400">
            No document libraries found in this site.
          </div>
        )}
      </div>
    </div>
  )

  const renderFileList = () => {
    const sortedFiles = getSortedFiles()

    return (
      <div className="overflow-hidden rounded-lg border">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-white/5">
            <tr>
              <th className="w-10 px-4 py-3"></th>
              <th
                className="cursor-pointer px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase select-none hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center gap-1">
                  Name
                  {renderSortIcon('name')}
                </div>
              </th>
              <th
                className="hidden cursor-pointer px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase select-none hover:bg-gray-100 md:table-cell dark:text-gray-400 dark:hover:bg-gray-700"
                onClick={() => handleSort('lastModified')}
              >
                <div className="flex items-center gap-1">
                  Modified
                  {renderSortIcon('lastModified')}
                </div>
              </th>
              <th
                className="hidden cursor-pointer px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase select-none hover:bg-gray-100 md:table-cell dark:text-gray-400 dark:hover:bg-gray-700"
                onClick={() => handleSort('size')}
              >
                <div className="flex items-center gap-1">
                  Size
                  {renderSortIcon('size')}
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
            {sortedFiles.map((node) => {
              const key = `${activeTab}-${node.id}`
              const isSelected = selectedItems.has(key)

              return (
                <tr
                  key={node.id}
                  className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900 ${isSelected ? 'bg-blue-50 dark:bg-blue-900/30' : ''}`}
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelection(node)}
                      onClick={(e) => e.stopPropagation()}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600"
                    />
                  </td>
                  <td
                    className="px-4 py-3"
                    onClick={() => (node.type === 'folder' ? navigateToFolder(node) : toggleSelection(node))}
                  >
                    <div className="flex items-center gap-3">
                      {node.type === 'folder' ? (
                        <div className="relative flex-shrink-0">
                          <Folder className="h-5 w-5 text-yellow-500" />
                          {/* Folder digest status checkmark overlay */}
                          {(() => {
                            const folderStatus = getFolderDigestStatus(node)
                            if (folderStatus === 'digested') {
                              return (
                                <CheckCircle className="absolute -right-1 -bottom-1 h-3 w-3 rounded-full bg-white text-green-600 dark:bg-gray-800" />
                              )
                            } else if (folderStatus === 'sync-required') {
                              return (
                                <AlertCircle className="absolute -right-1 -bottom-1 h-3 w-3 rounded-full bg-white text-amber-500 dark:bg-gray-800" />
                              )
                            }
                            return null
                          })()}
                        </div>
                      ) : (
                        <File className="h-5 w-5 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                      )}
                      <span className="truncate text-gray-900 dark:text-gray-100">{node.name}</span>
                      {/* Digest Status Tag for Files */}
                      {node.type === 'file' &&
                        (() => {
                          const digestStatus = getDigestStatus(node)
                          if (digestStatus === 'digested') {
                            return (
                              <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/40 dark:text-green-300">
                                <CheckCircle className="h-3 w-3" />
                                Digested
                              </span>
                            )
                          } else if (digestStatus === 'sync-required') {
                            return (
                              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                                <AlertCircle className="h-3 w-3" />
                                Sync Required
                              </span>
                            )
                          }
                          return null
                        })()}
                      {/* Digest Status Tag for Folders (text label) */}
                      {node.type === 'folder' &&
                        (() => {
                          const folderStatus = getFolderDigestStatus(node)
                          if (folderStatus === 'digested') {
                            return (
                              <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/40 dark:text-green-300">
                                Digested
                              </span>
                            )
                          } else if (folderStatus === 'sync-required') {
                            return (
                              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                                Sync Required
                              </span>
                            )
                          }
                          return null
                        })()}
                      {node.type === 'folder' && (
                        <ChevronRight className="ml-auto h-4 w-4 text-gray-400 dark:text-gray-500" />
                      )}
                    </div>
                  </td>
                  <td className="hidden px-4 py-3 text-sm text-gray-500 md:table-cell dark:text-gray-400">
                    {formatDate(node.lastModified)}
                  </td>
                  <td className="hidden px-4 py-3 text-sm text-gray-500 md:table-cell dark:text-gray-400">
                    {node.type === 'file' ? formatFileSize(node.size) : '--'}
                  </td>
                </tr>
              )
            })}
            {sortedFiles.length === 0 && !loading && (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                  No files found in this location
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    )
  }

  const renderSelectedItems = () => {
    if (selectedItems.size === 0) {
      return null
    }

    return (
      <div className="mt-4 rounded-lg bg-blue-50 p-4 dark:bg-blue-900/30">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="font-medium text-blue-900 dark:text-blue-300">Selected Items ({selectedItems.size})</h3>
          <button
            onClick={clearSelection}
            className="text-sm text-blue-600 hover:text-blue-800 dark:hover:text-blue-300"
          >
            Clear All
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {Array.from(selectedItems.values()).map((item) => (
            <span
              key={`${item.source}-${item.id}`}
              className="inline-flex items-center gap-1 rounded border border-blue-200 bg-white px-2 py-1 text-sm dark:border-blue-800/50 dark:bg-gray-800"
            >
              {item.type === 'folder' ? (
                <Folder className="h-3 w-3 text-yellow-500" />
              ) : (
                <File className="h-3 w-3 text-gray-400 dark:text-gray-500" />
              )}
              <span className="max-w-[150px] truncate">{item.name}</span>
              <button
                onClick={() => {
                  const newSelected = new Map(selectedItems)
                  newSelected.delete(`${item.source}-${item.id}`)
                  setSelectedItems(newSelected)
                }}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-400"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      </div>
    )
  }

  // Get digest status for a stored document (for the Digested Files table)
  const getStoredDocDigestStatus = (doc: StoredDocument): 'digested' | 'sync-required' => {
    // Check if we have file info in the current files list
    const currentFile = files.find((f) => f.name === doc.file_name.split('/').pop())
    if (currentFile && currentFile.lastModified) {
      const fileModified = new Date(currentFile.lastModified).getTime()
      const digestedAt = new Date(doc.updated_at).getTime()
      if (fileModified > digestedAt) {
        return 'sync-required'
      }
    }
    return 'digested'
  }

  // Toggle selection for digested files table
  const toggleDigestedFileSelection = (id: number) => {
    const newSelected = new Set(selectedDigestedFiles)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelectedDigestedFiles(newSelected)
  }

  // Select all on current page
  const toggleSelectAllDigested = () => {
    const startIndex = (digestedPage - 1) * digestedPageSize
    const endIndex = startIndex + digestedPageSize
    const pageDocIds = storedDocuments.slice(startIndex, endIndex).map((d) => d.id)

    const allSelected = pageDocIds.every((id) => selectedDigestedFiles.has(id))

    if (allSelected) {
      // Deselect all on this page
      const newSelected = new Set(selectedDigestedFiles)
      pageDocIds.forEach((id) => newSelected.delete(id))
      setSelectedDigestedFiles(newSelected)
    } else {
      // Select all on this page
      const newSelected = new Set(selectedDigestedFiles)
      pageDocIds.forEach((id) => newSelected.add(id))
      setSelectedDigestedFiles(newSelected)
    }
  }

  // Handle resync of selected files — queued like any other digest job.
  const handleResync = async () => {
    if (selectedDigestedFiles.size === 0) {
      toast.warning('Please select files to resync')
      return
    }

    setResyncInProgress(true)
    try {
      // Get the selected documents
      const docsToResync = storedDocuments.filter((doc) => selectedDigestedFiles.has(doc.id))

      // Build items for digest API - parse file_path to get driveId and fileId
      const items = docsToResync.map((doc) => {
        const pathParts = doc.file_path.split('/')
        return {
          id: pathParts[pathParts.length - 1] || doc.file_path,
          driveId: pathParts.length > 1 ? pathParts[0] : undefined,
          source: doc.source as SourceTab,
          folderPath: '',
        }
      })

      const job = await api.digestFiles(items, `Resync — ${items.length} file${items.length === 1 ? '' : 's'}`)
      toast.success(
        job.queuePosition > 0
          ? `Resync queued (#${job.queuePosition} in line) — track it in Digest Jobs`
          : 'Resync started — track it in Digest Jobs',
      )
      setSelectedDigestedFiles(new Set())
      await loadJobs()
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to queue resync')
      console.error('Error queueing resync:', error)
    } finally {
      setResyncInProgress(false)
    }
  }

  // ── Digest Jobs panel ──────────────────────────────────────────────────────
  // Every digest is a queued server-side job, so this is the one place to see
  // what is waiting, what is running, and what finished — no request is ever
  // held open long enough to time out.
  const renderJobsPanel = () => {
    const live = jobs.filter((j) => j.status === 'pending' || j.status === 'processing')
    const finished = jobs.filter((j) => j.status !== 'pending' && j.status !== 'processing')

    const statusChip = (job: DigestJob) => {
      const map: Record<DigestJob['status'], { label: string; className: string; icon: React.ReactNode }> = {
        pending: {
          label: job.queuePosition && job.queuePosition > 0 ? `Queued #${job.queuePosition}` : 'Queued',
          className: 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300',
          icon: <Clock className="h-3 w-3" />,
        },
        processing: {
          label: 'Running',
          className: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300',
          icon: <Loader className="h-3 w-3 animate-spin" />,
        },
        completed: {
          label: 'Completed',
          className: 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300',
          icon: <CheckCircle className="h-3 w-3" />,
        },
        failed: {
          label: 'Failed',
          className: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300',
          icon: <AlertCircle className="h-3 w-3" />,
        },
        cancelled: {
          label: 'Cancelled',
          className: 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
          icon: <Ban className="h-3 w-3" />,
        },
      }
      const chip = map[job.status]
      return (
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${chip.className}`}
        >
          {chip.icon}
          {chip.label}
        </span>
      )
    }

    const renderJobRow = (job: DigestJob) => {
      const pct = job.total > 0 ? Math.round((job.completed / job.total) * 100) : job.status === 'completed' ? 100 : 0
      const isLive = job.status === 'pending' || job.status === 'processing'
      // Walking a big SharePoint tree can take many minutes before the file count
      // is known. Say so, instead of reporting the number of items selected — two
      // folders holding 500 files otherwise read as "it only found 2 things".
      const isExpanding = job.status === 'processing' && job.currentFile === 'Expanding folders…'
      return (
        <div key={job.jobId} className="border-t border-gray-100 px-4 py-3 first:border-t-0 dark:border-gray-700">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                {statusChip(job)}
                <span
                  className="truncate text-sm font-medium text-gray-900 dark:text-gray-100"
                  title={job.label || job.jobId}
                >
                  {job.label || job.jobId}
                </span>
              </div>
              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {isExpanding
                  ? job.total > 0
                    ? `Scanning folders — ${job.total} files found so far…`
                    : 'Scanning folders…'
                  : job.total > 0
                    ? `${job.completed} of ${job.total} files`
                    : `${job.itemCount} item${job.itemCount === 1 ? '' : 's'} selected`}
                {job.createdAt && ` · queued ${new Date(job.createdAt).toLocaleString()}`}
              </div>
            </div>
            <button
              onClick={() => handleCancelJob(job)}
              title={isLive ? 'Cancel this job' : 'Remove from list'}
              className="flex items-center gap-1 rounded px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 hover:text-red-600 dark:text-gray-400 dark:hover:bg-gray-700"
            >
              {isLive ? <Ban className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
              {isLive ? 'Cancel' : 'Remove'}
            </button>
          </div>

          {isLive && (
            <div className="mt-2">
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                {isExpanding ? (
                  // Indeterminate: the total isn't known yet, so any percentage
                  // would be a lie.
                  <div className="h-1.5 w-1/3 animate-pulse rounded-full bg-blue-400" />
                ) : (
                  <div
                    className="h-1.5 rounded-full bg-blue-600 transition-all duration-300"
                    style={{ width: `${job.status === 'pending' ? 0 : pct}%` }}
                  />
                )}
              </div>
              {job.currentFile && !isExpanding && (
                <div className="mt-1 truncate text-xs text-gray-500 dark:text-gray-400" title={job.currentFile}>
                  {job.currentFile.split('/').pop()}
                </div>
              )}
            </div>
          )}

          {job.status === 'failed' && job.error && (
            <div className="mt-2 rounded bg-red-50 px-2 py-1 text-xs text-red-700 dark:bg-red-900/30 dark:text-red-300">
              {job.error}
            </div>
          )}
        </div>
      )
    }

    return (
      <div className="mt-6 rounded-lg bg-white p-6 shadow-md dark:bg-gray-800 dark:shadow-none">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-xl font-semibold">
            <ListChecks className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            Digest Jobs
            {live.length > 0 && (
              <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
                {live.length} active
              </span>
            )}
            {jobsStale && (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
                title="The last refresh failed. Jobs keep running on the server; this panel is retrying."
              >
                <AlertCircle className="h-3 w-3" />
                Reconnecting…
              </span>
            )}
          </h2>
          <div className="flex gap-2">
            <button
              onClick={() => loadJobs(true)}
              disabled={jobsLoading}
              className="flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-1.5 text-sm hover:bg-gray-200 disabled:opacity-50 dark:bg-gray-700 dark:hover:bg-gray-700"
            >
              <RefreshCw className={`h-4 w-4 ${jobsLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={handleClearFinishedJobs}
              disabled={finished.length === 0}
              className="flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-1.5 text-sm hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-700 dark:hover:bg-gray-700"
            >
              <Trash2 className="h-4 w-4" />
              Clear finished
            </button>
          </div>
        </div>

        {jobs.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
            No digest jobs yet. Select files above and click &ldquo;Digest Selected&rdquo; — jobs run in the background,
            so you can queue several and leave this page.
          </p>
        ) : (
          <div className="max-h-96 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700">
            {[...live, ...finished].map(renderJobRow)}
          </div>
        )}
      </div>
    )
  }

  const renderDigestedFilesTable = () => {
    if (storedDocuments.length === 0 && digestedFiles.length === 0) {
      return null
    }

    // Distinct classification labels across all docs, for the filter dropdown
    const classOptions = Array.from(
      new Set(storedDocuments.flatMap((d) => (d.classifications ?? []).map((c) => c.label))),
    ).sort()

    // Apply search (file name + summary) and classification filter, then paginate
    const search = digestedSearch.trim().toLowerCase()
    const filteredDocuments = storedDocuments.filter((d) => {
      const matchesSearch =
        !search || d.file_name?.toLowerCase().includes(search) || d.summary?.toLowerCase().includes(search)
      const matchesClass =
        digestedClassFilter === 'all' || (d.classifications ?? []).some((c) => c.label === digestedClassFilter)
      return matchesSearch && matchesClass
    })

    // Use filtered stored documents for the main table (persisted in database)
    const totalDocuments = filteredDocuments.length
    const totalPages = Math.ceil(totalDocuments / digestedPageSize)
    const startIndex = (digestedPage - 1) * digestedPageSize
    const endIndex = startIndex + digestedPageSize
    const paginatedDocs = filteredDocuments.slice(startIndex, endIndex)

    // Check if all items on current page are selected
    const pageDocIds = paginatedDocs.map((d) => d.id)
    const allPageSelected = pageDocIds.length > 0 && pageDocIds.every((id) => selectedDigestedFiles.has(id))
    const somePageSelected = pageDocIds.some((id) => selectedDigestedFiles.has(id))

    // File type color mapping
    const getFileTypeColor = (fileType?: string) => {
      const colorMap: { [key: string]: string } = {
        PDF: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
        Word: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
        Excel: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
        PowerPoint: 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300',
        Text: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
        Markdown: 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300',
        CSV: 'bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300',
        JSON: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300',
        JavaScript: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300',
        TypeScript: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
        Python: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
      }
      return colorMap[fileType || ''] || 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
    }

    // Get file type from filename
    const getFileTypeFromName = (fileName: string): string => {
      const ext = fileName.split('.').pop()?.toLowerCase() || ''
      const typeMap: { [key: string]: string } = {
        pdf: 'PDF',
        docx: 'Word',
        doc: 'Word',
        xlsx: 'Excel',
        xls: 'Excel',
        pptx: 'PowerPoint',
        ppt: 'PowerPoint',
        txt: 'Text',
        md: 'Markdown',
        csv: 'CSV',
        json: 'JSON',
        js: 'JavaScript',
        ts: 'TypeScript',
        py: 'Python',
      }
      return typeMap[ext] || ext.toUpperCase() || 'Unknown'
    }

    return (
      <div className="mt-6 rounded-lg bg-white p-6 shadow-md dark:bg-gray-800 dark:shadow-none">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-semibold">Digested Files</h2>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {totalDocuments} file{totalDocuments !== 1 ? 's' : ''}
            </span>
            {selectedDigestedFiles.size > 0 && (
              <span className="text-sm font-medium text-blue-600">{selectedDigestedFiles.size} selected</span>
            )}
          </div>
          <div className="flex gap-2">
            {selectedDigestedFiles.size > 0 && (
              <button
                onClick={handleResync}
                disabled={resyncInProgress}
                className="flex items-center gap-2 rounded-lg border border-blue-200 px-3 py-1.5 text-sm text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-700 disabled:opacity-50 dark:border-blue-800/50 dark:hover:bg-blue-900/30 dark:hover:text-blue-300"
              >
                {resyncInProgress ? <Loader className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                Resync Selected
              </button>
            )}
            <button
              onClick={() => {
                setDigestedFiles([])
                localStorage.removeItem('digestedFiles')
              }}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-700 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-900 dark:hover:text-gray-300"
            >
              Clear Activity Log
            </button>
            {isSuperadmin && (
              <button
                onClick={handlePurgeDomain}
                disabled={purgingDomain}
                title="Superadmin: delete ALL documents, embeddings, and job state for this domain (every user)"
                className="flex items-center gap-2 rounded-lg border border-red-200 px-3 py-1.5 text-sm text-red-600 transition-colors hover:bg-red-50 hover:text-red-700 disabled:opacity-50 dark:border-red-800/50 dark:hover:bg-red-900/30 dark:hover:text-red-300"
              >
                {purgingDomain ? <Loader className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                Purge {activeContext?.display_name || 'Domain'}
              </button>
            )}
          </div>
        </div>

        {/* Filter + search */}
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="relative min-w-[220px] flex-1">
            <Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              value={digestedSearch}
              onChange={(e) => setDigestedSearch(e.target.value)}
              placeholder="Search by file name or summary…"
              className="w-full rounded-lg border border-gray-200 py-2 pr-3 pl-9 text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-400 focus:outline-none dark:border-gray-700"
            />
          </div>
          <select
            value={digestedClassFilter}
            onChange={(e) => setDigestedClassFilter(e.target.value)}
            className="rounded-lg border border-gray-200 py-2 pr-8 pl-3 text-sm text-gray-700 focus:border-blue-400 focus:ring-1 focus:ring-blue-400 focus:outline-none dark:border-gray-700 dark:text-gray-300"
          >
            <option value="all">All classifications</option>
            {classOptions.map((label) => (
              <option key={label} value={label}>
                {label}
              </option>
            ))}
          </select>
          {(digestedSearch || digestedClassFilter !== 'all') && (
            <button
              onClick={() => {
                setDigestedSearch('')
                setDigestedClassFilter('all')
              }}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-900 dark:hover:text-gray-300"
            >
              Clear
            </button>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-white/5">
              <tr>
                <th className="w-10 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={allPageSelected}
                    ref={(input) => {
                      if (input) {
                        input.indeterminate = somePageSelected && !allPageSelected
                      }
                    }}
                    onChange={toggleSelectAllDigested}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400">
                  File Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400">
                  Source
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400">
                  Digested At
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400">
                  Ingested By
                </th>
                <th className="min-w-[300px] px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400">
                  Summary
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400">
                  Classifications
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
              {paginatedDocs.map((doc) => {
                const isSelected = selectedDigestedFiles.has(doc.id)
                const digestStatus = getStoredDocDigestStatus(doc)
                const fileType = getFileTypeFromName(doc.file_name)

                return (
                  <tr
                    key={doc.id}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-900 ${isSelected ? 'bg-blue-50 dark:bg-blue-900/30' : ''}`}
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleDigestedFileSelection(doc.id)}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600"
                      />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {digestStatus === 'digested' ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/40 dark:text-green-300">
                          <CheckCircle className="h-3 w-3" />
                          Digested
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                          <AlertCircle className="h-3 w-3" />
                          Sync Required
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`rounded px-2 py-1 text-xs font-medium ${getFileTypeColor(fileType)}`}>
                        {fileType}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div
                        className="max-w-[250px] truncate text-sm text-gray-900 dark:text-gray-100"
                        title={doc.file_name}
                      >
                        {doc.file_name}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm whitespace-nowrap text-gray-500 capitalize dark:text-gray-400">
                      {doc.source || 'onedrive'}
                    </td>
                    <td className="px-4 py-3 text-xs whitespace-nowrap text-gray-500 dark:text-gray-400">
                      {new Date(doc.updated_at).toLocaleString()}
                    </td>
                    <td
                      className="px-4 py-3 text-xs whitespace-nowrap text-gray-500 dark:text-gray-400"
                      title={doc.user_email}
                    >
                      {doc.user_email === user?.username ? 'You' : (doc.user_email?.split('@')[0] ?? '—')}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {doc.summary ? (
                        <span className="line-clamp-2 text-gray-700 dark:text-gray-300" title={doc.summary}>
                          {doc.summary}
                        </span>
                      ) : (
                        <span className="text-gray-400 italic dark:text-gray-500">No summary available</span>
                      )}
                    </td>
                    <td className="px-4 py-3">{renderClassificationBadges(doc.classifications)}</td>
                  </tr>
                )
              })}
              {paginatedDocs.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    No digested files yet. Select files from the File Browser and click "Digest Selected".
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4 dark:border-gray-700">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Showing {startIndex + 1}-{Math.min(endIndex, totalDocuments)} of {totalDocuments}
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setDigestedPage(1)}
                disabled={digestedPage === 1}
                className="rounded p-1 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-gray-700"
                title="First page"
              >
                <ChevronsLeft className="h-5 w-5" />
              </button>
              <button
                onClick={() => setDigestedPage((p) => Math.max(1, p - 1))}
                disabled={digestedPage === 1}
                className="rounded p-1 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-gray-700"
                title="Previous page"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <span className="px-3 py-1 text-sm">
                Page {digestedPage} of {totalPages}
              </span>
              <button
                onClick={() => setDigestedPage((p) => Math.min(totalPages, p + 1))}
                disabled={digestedPage === totalPages}
                className="rounded p-1 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-gray-700"
                title="Next page"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
              <button
                onClick={() => setDigestedPage(totalPages)}
                disabled={digestedPage === totalPages}
                className="rounded p-1 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-gray-700"
                title="Last page"
              >
                <ChevronsRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Data Digest</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Browse and digest files from OneDrive and SharePoint for KT AI Assistant context
          </p>

          {/* Which knowledge base this page is acting on. Everything here — the
              documents, the jobs, and where a new digest lands — belongs to this
              context, and it is shared with everyone in it. Worth stating plainly:
              the only other cue is the switcher up in the nav. */}
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-4 py-2.5 dark:bg-blue-900/30">
            <Database className="h-4 w-4 shrink-0 text-blue-600" />
            <span className="text-sm text-blue-900 dark:text-blue-300">
              Digesting into{' '}
              <span className="font-semibold">
                {activeContext?.display_name ?? activeContext?.name ?? 'no context'}
              </span>
            </span>
            <span className="text-xs text-blue-700 dark:text-blue-300">
              · files land in this context and are available to everyone who belongs to it
            </span>
            {availableContexts.length > 1 && (
              <span className="ml-auto text-xs text-blue-700 dark:text-blue-300">
                Switch context from the menu above
              </span>
            )}
          </div>
        </div>

        {/* File Browser */}
        <div className="rounded-lg bg-white p-6 shadow-md dark:bg-gray-800 dark:shadow-none">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-xl font-semibold">
              File Browser
              {user && (
                <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                  ({user.name || user.username})
                </span>
              )}
            </h2>
            <div className="flex gap-2">
              <button
                onClick={refreshCurrentFolder}
                disabled={loading}
                className="flex items-center gap-2 rounded-lg bg-gray-100 px-4 py-2 hover:bg-gray-200 disabled:opacity-50 dark:bg-gray-700 dark:hover:bg-gray-700"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                onClick={handleDigest}
                disabled={submitting || selectedItems.size === 0}
                className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {submitting ? <Loader className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                Digest Selected ({selectedItems.size})
              </button>
            </div>
          </div>

          {/* Tabs */}
          {renderTabs()}

          {/* Breadcrumbs */}
          {breadcrumbs.length > 0 && renderBreadcrumbs()}

          {/* Content */}
          <div className="max-h-[600px] min-h-[400px] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader className="h-8 w-8 animate-spin text-gray-400 dark:text-gray-500" />
              </div>
            ) : activeTab === 'sharepoint' && !selectedSite ? (
              renderSharePointSiteSelector()
            ) : activeTab === 'sharepoint' && selectedSite && !selectedLibrary ? (
              renderLibrarySelector()
            ) : (
              renderFileList()
            )}
          </div>

          {/* Selected Items */}
          {renderSelectedItems()}
        </div>

        {/* Digest Jobs */}
        {renderJobsPanel()}

        {/* Digested Files Table */}
        {renderDigestedFilesTable()}
      </div>
    </div>
  )
}

export default ContextDigester
